import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarRange, Send, Upload } from "lucide-react";
import * as api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useAction, useData } from "@/hooks/useData";
import { Button, DefinitionList, Field, Input, SectionCard, Segmented, Select, Textarea } from "@/components/ui";
import { LEAVE_TYPES } from "@/data/seed";

export default function ApplyLeave() {
  const { user } = useAuth();
  const nav = useNavigate();
  const { run, pending } = useAction();
  const { data: employee } = useData(() => api.employees.get(user.employee_id), [user.employee_id]);
  const { data: balances } = useData(() => api.leaves.balance(user.employee_id), [user.employee_id]);

  const [form, setForm] = useState({ leave_type: "Annual", duration: "full", from_date: "", to_date: "", reason: "" });
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const half = form.duration === "half";
  const toDate = half ? form.from_date : form.to_date;
  const days = !form.from_date || !toDate || toDate < form.from_date
    ? 0
    : half ? 0.5 : Math.round((new Date(toDate) - new Date(form.from_date)) / 86400000) + 1;
  const balance = balances?.find((b) => b.leave_type === form.leave_type)?.balance ?? 0;
  const limited = !["Unpaid", "Half Day"].includes(form.leave_type);
  const overBalance = limited && days > balance;

  const submit = async (e) => {
    e.preventDefault();
    const { ok } = await run(() => api.leaves.apply({
      employee_id: user.employee_id, leave_type: form.leave_type, from_date: form.from_date,
      to_date: toDate, half_day: half, reason: form.reason,
    }), "Leave request submitted");
    if (ok) nav("/leave/history");
  };

  return (
    <form onSubmit={submit} className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2 space-y-4">
        <SectionCard title="Employee">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Name"><Input value={employee ? `${employee.name} (${employee.id})` : ""} disabled /></Field>
            <Field label="Department"><Input value={employee?.department || ""} disabled /></Field>
          </div>
        </SectionCard>

        <SectionCard title="Leave Details">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Leave type" hint={limited ? `${balance} days available` : "Not deducted from a balance"}>
              <Select value={form.leave_type} onChange={set("leave_type")} options={LEAVE_TYPES} />
            </Field>
            <div>
              <span className="label">Duration</span>
              <Segmented value={form.duration} onChange={set("duration")} className="pb-0" options={[{ value: "full", label: "Full day(s)" }, { value: "half", label: "Half day" }]} />
            </div>
            <Field label={half ? "Date" : "From"}><Input type="date" value={form.from_date} onChange={set("from_date")} required /></Field>
            {!half && <Field label="To"><Input type="date" value={form.to_date} min={form.from_date || undefined} onChange={set("to_date")} required /></Field>}
            <Field label="Reason" className="md:col-span-2">
              <Textarea value={form.reason} onChange={set("reason")} placeholder="Briefly describe the reason for your leave" required />
            </Field>
            <div className="md:col-span-2">
              <span className="label">Attachment</span>
              <div className="neu-inset p-6 text-center text-slate-400">
                <Upload className="mx-auto mb-2" size={20} />
                <p className="text-xs">Medical certificates and other attachments aren't supported in the demo.</p>
              </div>
            </div>
          </div>
        </SectionCard>
      </div>

      <div className="space-y-4 lg:sticky lg:top-4 self-start">
        <SectionCard title="Summary">
          <DefinitionList rows={[
            { label: "Type", value: form.leave_type },
            { label: "Duration", value: half ? "Half day" : "Full day(s)" },
            { label: "Days", value: days },
            ...(limited ? [{ label: "Balance after", value: <span className={overBalance ? "text-rose-600" : ""}>{Math.max(balance - days, 0)} days</span> }] : []),
          ]} />
          {overBalance && <p className="text-[11px] text-rose-600 mt-3">This exceeds your available balance. It can still be submitted for review.</p>}
          <div className="neu-divider my-4" />
          <div className="flex gap-2">
            <Button className="flex-1" onClick={() => nav(-1)}>Cancel</Button>
            <Button type="submit" variant="primary" className="flex-1" icon={<Send size={13} />} loading={pending} disabled={!days}>Submit</Button>
          </div>
        </SectionCard>
        <div className="neu-inset p-4 text-[11px] text-slate-500 flex gap-2">
          <CalendarRange size={14} className="text-brand-orange shrink-0 mt-0.5" />
          <p>Leave cycle runs <b>1 Jan – 31 Dec</b>. Sick leave is paid 15 days full, 30 half and 45 unpaid under UAE Labour Law.</p>
        </div>
      </div>
    </form>
  );
}
